import React from 'react';
import { Link } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { ExternalLink, Pencil, UserRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatRial } from '@/lib/formatMoney';
import {
  ACCOUNT_LEVEL_LABELS,
  resolveAccountHierarchy,
} from '@/lib/accountHeadingHierarchy';
import type { ExpenseEntry } from '@/lib/mock/expenses';
import { accountingDocumentHref } from '@/lib/accountingDocumentProjectScope';
import CounterpartySideBadge from '@/components/document-entry/CounterpartySideBadge';
import { employeeProfilePath, parseCounterpartyKey, projectBeneficiaryPath } from '@/pages/project-workspace/projectWorkspaceHelpers';

function AccountHierarchyCell({
  accountCode,
  accountTitle,
  accountTitleByCode,
}: {
  accountCode?: string;
  accountTitle?: string;
  accountTitleByCode: Record<string, string>;
}) {
  const hierarchy = resolveAccountHierarchy(accountCode || '', accountTitleByCode, accountTitle);
  const rows = ACCOUNT_LEVEL_LABELS.map(({ key, label }) => ({
    label,
    data: hierarchy[key],
  })).filter((x) => x.data?.code);

  if (!rows.length) return <span className="text-slate-400">—</span>;

  return (
    <div className="min-w-[11rem] space-y-1 rounded-md border border-slate-100 bg-slate-50/80 px-2 py-1.5 text-xs">
      {rows.map(({ label, data }) => (
        <div key={label} className="grid grid-cols-[2.5rem_3rem_1fr] items-start gap-1">
          <span className="text-slate-400">{label}</span>
          <span className="font-mono font-medium text-slate-800" dir="ltr">
            {data!.code}
          </span>
          <span className="leading-snug text-slate-700">{data!.title || '—'}</span>
        </div>
      ))}
    </div>
  );
}

export function createProjectExpenseColumns(opts: {
  projectId?: number;
  linkBeneficiary?: boolean;
  hideBeneficiaryColumn?: boolean;
  accountTitleByCode?: Record<string, string>;
  compact?: boolean;
}): ColumnDef<ExpenseEntry>[] {
  const {
    projectId,
    linkBeneficiary,
    hideBeneficiaryColumn,
    accountTitleByCode = {},
    compact = false,
  } = opts;

  const cols: ColumnDef<ExpenseEntry>[] = [
    {
      accessorKey: 'dateFa',
      header: 'تاریخ',
      cell: ({ row }) => (
        <span className="whitespace-nowrap font-mono text-sm" dir="ltr">
          {row.original.dateFa || '—'}
        </span>
      ),
    },
    {
      accessorKey: 'docNo',
      header: 'سند',
      cell: ({ row }) =>
        row.original.docNo ? (
          <span className="font-mono text-sm text-slate-700" dir="ltr">
            {row.original.docNo}
          </span>
        ) : (
          <span className="text-slate-400">—</span>
        ),
    },
  ];

  if (!hideBeneficiaryColumn) {
    cols.push({
      accessorKey: 'beneficiary',
      header: 'طرف / نقش',
      cell: ({ row }) => {
        const key = row.original.beneficiaryId;
        const label = row.original.beneficiary || '—';
        const inner = (
          <div className="max-w-[11rem] space-y-1">
            <div className="flex items-center gap-2">
              <UserRound className="h-4 w-4 shrink-0 text-slate-400" />
              <span className="truncate font-medium text-slate-700">{label}</span>
            </div>
            {key ? (
              <CounterpartySideBadge
                debit={Number(row.original.debit ?? 0)}
                credit={Number(row.original.credit ?? 0)}
              />
            ) : null}
          </div>
        );
        if (linkBeneficiary && key) {
          const cp = parseCounterpartyKey(key);
          const href =
            cp?.type === 'employee'
              ? employeeProfilePath(cp.id)
              : projectId
                ? projectBeneficiaryPath(projectId, key)
                : null;
          if (href) {
            return (
              <Link to={href} className="inline-flex rounded-md transition-colors hover:bg-emerald-50 hover:text-emerald-800">
                {inner}
              </Link>
            );
          }
        }
        return inner;
      },
    });
  }

  cols.push(
    {
      id: 'accounts',
      header: 'حساب‌ها (کل / معین / تفصیلی)',
      cell: ({ row }) => (
        <AccountHierarchyCell
          accountCode={row.original.accountCode}
          accountTitle={row.original.accountTitle}
          accountTitleByCode={accountTitleByCode}
        />
      ),
    },
    {
      accessorKey: 'debit',
      header: 'بدهکار',
      cell: ({ row }) => {
        const debit = Number(row.original.debit ?? 0);
        if (debit > 0) {
          return <span className="whitespace-nowrap font-mono text-sm font-medium text-rose-700">{formatRial(debit)}</span>;
        }
        const amount = Number(row.original.amount ?? 0);
        if (amount > 0 && !row.original.credit) {
          return <span className="whitespace-nowrap font-mono text-sm font-medium text-rose-700">{formatRial(amount)}</span>;
        }
        return <span className="text-slate-400">—</span>;
      },
    },
    {
      accessorKey: 'credit',
      header: 'بستانکار',
      cell: ({ row }) => {
        const credit = Number(row.original.credit ?? 0);
        if (credit <= 0) return <span className="text-slate-400">—</span>;
        return <span className="whitespace-nowrap font-mono text-sm font-medium text-emerald-700">{formatRial(credit)}</span>;
      },
    },
    {
      accessorKey: 'description',
      header: 'شرح',
      cell: ({ row }) => (
        <span className="line-clamp-2 max-w-[14rem] text-sm text-slate-600">{row.original.description || '—'}</span>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) =>
        row.original.documentId ? (
          <div className="flex justify-center gap-1">
            <Button type="button" variant="ghost" size="icon" asChild title="مشاهده سند">
            <Link to={accountingDocumentHref(row.original.documentId, { projectId })}>
              <ExternalLink className="h-4 w-4" />
            </Link>
          </Button>
          <Button type="button" variant="ghost" size="icon" className="text-indigo-700" asChild title="ویرایش سند">
            <Link to={accountingDocumentHref(row.original.documentId, { projectId, edit: true })}>
                <Pencil className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        ) : null,
    },
  );

  if (!compact) {
    // reserved for extended columns if needed later
  }

  return cols;
}
